import React, { useMemo, useCallback } from 'react';
import { Button, Loader, Whisper, Tooltip } from 'rsuite';
import CheckIcon from '@rsuite/icons/Check';
import { useCartQuery } from '@/api/useCartQuery';
import { useUpdateCart } from '@/api/useUpdateCart';
import type { LineItem } from '@/types';
import styles from './VariantAddToCart.module.scss';
import { cn } from '@bem-react/classname';

export type AddToCartProps = {
  variantId: number | string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  appearance?: 'default' | 'primary' | 'link' | 'subtle' | 'ghost';
  circle?: boolean;
  className?: string;
  disabled?: boolean;
};

export default function VariantAddToCart({
  variantId,
  size = 'sm',
  appearance = 'primary',
  circle = true,
  className,
  disabled,
}: AddToCartProps) {
  const { data: cart } = useCartQuery();
  const updateCart = useUpdateCart();

  // BEM classname formatter for this component
  const cName = cn(styles.cVariantAddToCart);

  const { inCart, existingItem, currentQty } = useMemo(() => {
    const lineItems = cart?.lineItems || [];
    const item = lineItems.find((li: LineItem) => String(li.purchasableId) === String(variantId));
    const qty = item?.qty ?? 0;
    return { inCart: !!item && (qty ?? 0) > 0, existingItem: item, currentQty: qty };
  }, [cart?.lineItems, variantId]);

  const hasCartItems = useMemo(() => {
    const lineItems = cart?.lineItems || [];
    return lineItems.some((li: LineItem) => (Number(li?.qty) || 0) > 0);
  }, [cart?.lineItems]);

  const isLoading = !!updateCart.isPending;

  const handleClick = useCallback(() => {
    if (isLoading) return;

    // If line item exists in cart, increment its quantity. Otherwise, add new line item.
    if (existingItem && existingItem.id != null) {
      const nextQty = (Number(currentQty) || 0) + 1;
      updateCart.mutate({
        lineItems: {
          // Craft Commerce update-cart accepts lineItems[{id}][qty]
          [existingItem.id as LineItem['id']]: { qty: nextQty },
        },
      } as any);
    } else {
      updateCart.mutate({
        // Craft Commerce update-cart accepts root-level purchasableId & qty for adding new line items
        purchasableId: variantId,
        qty: 1,
      } as any);
    }
  }, [existingItem, currentQty, variantId, updateCart, isLoading]);

  const content = isLoading
    ? <Loader size="xs" />
    : (
      <>
        <span>{inCart ? 'Added' : 'Add to cart'}</span>
        {inCart ? <strong className={cName('Counter')}>({Number(currentQty) || 0})</strong> : null}
      </>
    );

  const ariaLabel = inCart ? 'Add one more' : 'Add to cart';

  const btn = (
    <Button
      size={size}
      color={inCart ? 'orange' : undefined}
      appearance={appearance}
      onClick={handleClick}
      aria-label={ariaLabel}
      disabled={!!disabled || isLoading}
      className={cName({inCart, isLoading}, [className])}
    >
      {content}
    </Button>
  );

  // Small tooltip for clarity on hover
  return inCart ? (
    <Whisper placement="top" speaker={<Tooltip>{ariaLabel}</Tooltip>}>
      {btn}
    </Whisper>
  ) : (
    btn
  );
}
